import * as React from 'react';

import * as hiveCore from '@gitbook/hive-core';
import { ContentOperationStatus } from '@gitbook/hive-core';
import { useHiveBranch, useHiveBranchOperation } from '@gitbook/hive-react';
import {
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    LoadingIndicator,
    toast,
} from '@gitbook/spine-react';

/**
 * Wrap content to display an alert when the space is being duplicated.
 * Displays an error notification when the duplicate operation goes in ERROR state.
 */
export function SpaceDuplicatingAlert(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;

    const branch = useHiveBranch();
    const branchOperation = useHiveBranchOperation(branch);

    // Tasks will send updates regardless of whether the operation failed, hence the useEffect
    React.useEffect(() => {
        if (branchOperation?.status === ContentOperationStatus.ERROR) {
            toast.error('Error while duplicating space', null);
        }
    }, [branchOperation?.status]);

    const isDuplicatingSpace =
        branchOperation !== null &&
        'type' in branchOperation.payload &&
        branchOperation.payload.type === 'duplicate' &&
        hiveCore.isContentOperationActive(branchOperation);

    if (!isDuplicatingSpace) {
        return <>{children}</>;
    }

    return (
        <EmptyState>
            <EmptyStateIcon>
                <LoadingIndicator />
            </EmptyStateIcon>
            <Heading>Copying content</Heading>
            <Description>
                Hang tight. This process may take a few seconds, up to a few minutes, depending on
                the number of pages and files.
            </Description>
        </EmptyState>
    );
}
